/**
 * Helpers for email-template Jinja variables: discovery, insert catalog,
 * and preview sample payloads.
 */

/** First identifier in a `{{ … }}` expression (ignores filters / extras). */
const JINJA_VAR_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)/g;

/**
 * `{% if name %}` / `{% elif not name %}` / `{% if name == 'x' %}` —
 * optional `not`, then the payload identifier (skips string-literal-first forms).
 */
const JINJA_IF_RE = /\{%\s*(?:el)?if\s+(?:not\s+)?([a-zA-Z_][a-zA-Z0-9_]*)/g;

/** `{% if 'foo' in name %}` — variable after `in`. */
const JINJA_IF_IN_RE =
  /\{%\s*(?:el)?if\s+(?:'[^']*'|"[^"]*")\s+in\s+([a-zA-Z_][a-zA-Z0-9_]*)/g;

/** `{% for item in list %}` — capture list (group 2) and item local (group 1). */
const JINJA_FOR_RE =
  /\{%\s*for\s+([a-zA-Z_][a-zA-Z0-9_]*)\s+in\s+([a-zA-Z_][a-zA-Z0-9_]*)/g;

/** Not payload keys — Jinja builtins / loop specials from `{{ loop.index }}`. */
const JINJA_RESERVED = new Set(['loop', 'true', 'false', 'none', 'True', 'False', 'None', 'not']);

/** HR-friendly labels for known template fields (# picker / Insert logic). */
export const VARIABLE_LABEL_BY_NAME = {
  candidate_name: 'Candidate name',
  full_name: 'Recipient full name',
  recipient_name: 'Recipient name',
  stage_label: 'Stage name',
  status_label: 'Status badge',
  role_label: 'Role',
  candidate_url: 'Candidate link',
  portal_url: 'Portal link',
  report_url: 'Client report link',
  panel_url: 'Panel page link',
  seb_config_url: 'SEB config download',
  seb_config_url_mcq: 'SEB config (IQ Assessment)',
  seb_config_url_tech: 'SEB config (Tech Assessment)',
  seb_download_url_windows: 'SEB download (Windows)',
  seb_download_url_macos: 'SEB download (macOS)',
  videobot_url: 'Video Bot link',
  mcq_url: 'IQ Assessment invitation link',
  tech_url: 'Tech Assessment invitation link',
  stages_label: 'Included stages',
  include_videobot: 'Include Video Bot section',
  include_seb_block: 'Include SEB block',
  include_mcq: 'Include IQ Assessment section',
  include_tech: 'Include Tech Assessment section',
  videobot_step: 'Video Bot section number',
  mcq_step: 'Analytical Skills section number',
  tech_step: 'Technical Skills section number',
  expires_at: 'Expiry time',
  require_otc: 'Requires one-time code',
  code: 'One-time code',
  ttl_minutes: 'Code validity (minutes)',
  panel_subject: 'Panel subject',
  overall_score: 'Overall score',
  recommendation: 'Recommendation',
  candidate_first_name: 'Candidate first name',
  candidate_last_name: 'Candidate last name',
  interview_header: 'Interview header',
  interview_date: 'Interview date',
  interview_time: 'Interview time',
  panelist_name: 'Panelist name',
  candidature_status_html: 'Candidature status (HTML)',
  candidature_status_text: 'Candidature status (text)',
  join_url: 'Meeting join link',
  guidelines: 'Interview guidelines',
  questions: 'Interview questions',
  round_label: 'Interview round',
  start_at_display: 'Start time',
  timezone_label: 'Timezone',
  feedback_url: 'Feedback form link',
};

/** Display label for a variable; falls back to the raw name. */
export function labelForVariable(name) {
  const key = String(name || '').trim();
  if (!key) return '';
  return VARIABLE_LABEL_BY_NAME[key] || key.replace(/_/g, ' ');
}

/** Well-known sample values keyed by variable name (system templates). */
export const SAMPLE_VALUE_BY_VAR = {
  candidate_name: 'Aanya Kapoor',
  full_name: 'Aarav Mehta',
  recipient_name: 'Aarav Mehta',
  stage_label: 'Video Bot',
  status_label: 'Completed',
  role_label: 'Senior Software Engineer',
  candidate_url: 'https://hireiq.kadellabs.com/candidate/9X7QABC4LM',
  portal_url: 'https://hireiq.kadellabs.com/portal',
  report_url: 'https://hireiq.kadellabs.com/client/abc-xyz',
  panel_url: 'https://hireiq.kadellabs.com/portal/candidates/abc?tab=panels',
  seb_config_url: 'https://api.hireiq.kadellabs.com/api/v1/seb/config/9X7QABC4LM.seb',
  seb_config_url_mcq: 'https://api.hireiq.kadellabs.com/api/v1/seb/config/MCQ9X7QABC.seb',
  seb_config_url_tech: 'https://api.hireiq.kadellabs.com/api/v1/seb/config/TECH9X7QAB.seb',
  seb_download_url_windows: 'https://safeexambrowser.org/download_en.html',
  seb_download_url_macos: 'https://safeexambrowser.org/download_en.html',
  videobot_url: 'https://hireiq.kadellabs.com/candidate/9X7QABC4LM/videobot',
  mcq_url: 'https://hireiq.kadellabs.com/candidate/MCQ9X7QABC/mcq',
  tech_url: 'https://hireiq.kadellabs.com/candidate/TECH9X7QAB/tech-assessment',
  stages_label: 'Video Bot, IQ Assessment, Tech Assessment',
  include_videobot: true,
  include_seb_block: true,
  include_mcq: true,
  include_tech: true,
  expires_at: '2026-07-01 18:00 UTC',
  require_otc: true,
  code: '482917',
  ttl_minutes: 10,
  panel_subject: 'Round 1 · Technical',
  overall_score: 4.2,
  recommendation: 'Hire',
  candidate_first_name: 'Aanya',
  candidate_last_name: 'Kapoor',
  interview_header: 'Tech Interview',
  interview_date: '14-06-2026',
  interview_time: '2:00 PM',
  panelist_name: 'Panel Lead',
  candidature_status_html: '<p><strong>Note#1:</strong> Video Assessment - In progress</p>',
  candidature_status_text: 'Video Assessment - In progress',
  join_url: 'https://teams.microsoft.com/l/meetup-join/example',
  guidelines: 'Focus on system design and recent project ownership.',
  questions: [
    'Walk us through a recent production incident you owned.',
    'How do you approach API design for multi-tenant services?',
  ],
  round_label: 'Technical Round 1',
  start_at_display: '3 Aug 2026, 11:00',
  timezone_label: 'Asia/Kolkata',
  feedback_url: 'https://hireiq.kadellabs.com/panel-feedback/example-token',
  resume_url: 'https://hireiq.kadellabs.com/panel-resume/example-token',
};

/** Codes that ship with a full canned sample payload. */
export const DEFAULT_SAMPLE_BY_CODE = {
  candidate_otc_link: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    stage_label: SAMPLE_VALUE_BY_VAR.stage_label,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    candidate_url: SAMPLE_VALUE_BY_VAR.candidate_url,
    expires_at: SAMPLE_VALUE_BY_VAR.expires_at,
  },
  candidate_otc_link_seb_mcq: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    stage_label: 'IQ Assessment',
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    candidate_url: 'https://hireiq.kadellabs.com/candidate/9X7QABC4LM/mcq',
    seb_config_url: SAMPLE_VALUE_BY_VAR.seb_config_url,
    seb_download_url_windows: SAMPLE_VALUE_BY_VAR.seb_download_url_windows,
    seb_download_url_macos: SAMPLE_VALUE_BY_VAR.seb_download_url_macos,
    expires_at: SAMPLE_VALUE_BY_VAR.expires_at,
  },
  candidate_otc_link_seb_tech: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    stage_label: 'Tech Assessment',
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    candidate_url: 'https://hireiq.kadellabs.com/candidate/9X7QABC4LM/tech-assessment',
    seb_config_url: SAMPLE_VALUE_BY_VAR.seb_config_url,
    seb_download_url_windows: SAMPLE_VALUE_BY_VAR.seb_download_url_windows,
    seb_download_url_macos: SAMPLE_VALUE_BY_VAR.seb_download_url_macos,
    expires_at: SAMPLE_VALUE_BY_VAR.expires_at,
  },
  candidate_combined_assessment_invite: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    stages_label: SAMPLE_VALUE_BY_VAR.stages_label,
    expires_at: SAMPLE_VALUE_BY_VAR.expires_at,
    include_videobot: true,
    videobot_step: 1,
    videobot_url: SAMPLE_VALUE_BY_VAR.videobot_url,
    include_seb_block: true,
    seb_download_url_windows: SAMPLE_VALUE_BY_VAR.seb_download_url_windows,
    seb_download_url_macos: SAMPLE_VALUE_BY_VAR.seb_download_url_macos,
    include_mcq: true,
    mcq_step: 2,
    mcq_url: SAMPLE_VALUE_BY_VAR.mcq_url,
    seb_config_url_mcq: SAMPLE_VALUE_BY_VAR.seb_config_url_mcq,
    include_tech: true,
    tech_step: 3,
    tech_url: SAMPLE_VALUE_BY_VAR.tech_url,
    seb_config_url_tech: SAMPLE_VALUE_BY_VAR.seb_config_url_tech,
    candidature_status_html: SAMPLE_VALUE_BY_VAR.candidature_status_html,
    candidature_status_text: SAMPLE_VALUE_BY_VAR.candidature_status_text,
  },
  client_share_link: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    report_url: SAMPLE_VALUE_BY_VAR.report_url,
    expires_at: '2026-07-05 18:00 UTC',
    require_otc: true,
  },
  client_share_otc_code: {
    code: SAMPLE_VALUE_BY_VAR.code,
    ttl_minutes: SAMPLE_VALUE_BY_VAR.ttl_minutes,
  },
  panel_evaluation_ready: {
    recipient_name: SAMPLE_VALUE_BY_VAR.recipient_name,
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    panel_subject: SAMPLE_VALUE_BY_VAR.panel_subject,
    overall_score: SAMPLE_VALUE_BY_VAR.overall_score,
    recommendation: SAMPLE_VALUE_BY_VAR.recommendation,
    panel_url: SAMPLE_VALUE_BY_VAR.panel_url,
  },
  assigned_hr_stage_completed: {
    recipient_name: SAMPLE_VALUE_BY_VAR.recipient_name,
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    stage_label: SAMPLE_VALUE_BY_VAR.stage_label,
    status_label: SAMPLE_VALUE_BY_VAR.status_label,
    portal_url: 'https://hireiq.kadellabs.com/portal/candidates/abc?evaluation_id=eval-1',
  },
  candidate_videobot_invite: {
    candidate_first_name: SAMPLE_VALUE_BY_VAR.candidate_first_name,
    candidate_last_name: SAMPLE_VALUE_BY_VAR.candidate_last_name,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    candidate_url: 'https://hireiq.kadellabs.com/candidate/9X7QABC4LM/videobot',
    expires_at: SAMPLE_VALUE_BY_VAR.expires_at,
    candidature_status_html: SAMPLE_VALUE_BY_VAR.candidature_status_html,
    candidature_status_text: SAMPLE_VALUE_BY_VAR.candidature_status_text,
  },
  candidate_tech_interview_invite: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    interview_header: SAMPLE_VALUE_BY_VAR.interview_header,
    interview_date: SAMPLE_VALUE_BY_VAR.interview_date,
    interview_time: SAMPLE_VALUE_BY_VAR.interview_time,
    timezone_label: SAMPLE_VALUE_BY_VAR.timezone_label,
    join_url: SAMPLE_VALUE_BY_VAR.join_url,
    candidature_status_html:
      '<p><strong>Note#1:</strong> Functional/Technical Interview - In progress</p>',
    candidature_status_text: 'Functional/Technical Interview - In progress',
  },
  panel_interviewer_briefing: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    round_label: SAMPLE_VALUE_BY_VAR.round_label,
    interview_header: SAMPLE_VALUE_BY_VAR.interview_header,
    interview_date: SAMPLE_VALUE_BY_VAR.interview_date,
    interview_time: SAMPLE_VALUE_BY_VAR.interview_time,
    timezone_label: SAMPLE_VALUE_BY_VAR.timezone_label,
    join_url: SAMPLE_VALUE_BY_VAR.join_url,
    guidelines: SAMPLE_VALUE_BY_VAR.guidelines,
    questions: SAMPLE_VALUE_BY_VAR.questions,
    panelist_name: SAMPLE_VALUE_BY_VAR.panelist_name,
    resume_url: SAMPLE_VALUE_BY_VAR.resume_url,
  },
  panel_feedback_invite: {
    candidate_name: SAMPLE_VALUE_BY_VAR.candidate_name,
    role_label: SAMPLE_VALUE_BY_VAR.role_label,
    round_label: SAMPLE_VALUE_BY_VAR.round_label,
    start_at_display: SAMPLE_VALUE_BY_VAR.start_at_display,
    timezone_label: SAMPLE_VALUE_BY_VAR.timezone_label,
    questions: SAMPLE_VALUE_BY_VAR.questions,
    join_url: SAMPLE_VALUE_BY_VAR.join_url,
    feedback_url: SAMPLE_VALUE_BY_VAR.feedback_url,
  },
  user_invite_welcome: {
    full_name: SAMPLE_VALUE_BY_VAR.full_name,
    role_label: 'HR Recruiter',
    portal_url: SAMPLE_VALUE_BY_VAR.portal_url,
  },
};

/** Catalog shown in the # variable picker (create + edit). */
export const COMMON_INSERT_VARIABLES = Object.keys(SAMPLE_VALUE_BY_VAR).sort();

/**
 * Collect unique payload variable names from template text.
 *
 * Includes `{{ name }}`, `{% if name %}`, and the list in `{% for item in list %}`.
 * Excludes loop item locals and Jinja reserved names so preview samples bind
 * real lists (e.g. `questions`) instead of omitting them.
 */
export function extractJinjaVars(...parts) {
  const found = new Set();
  const locals = new Set();
  for (const part of parts) {
    if (!part || typeof part !== 'string') continue;

    JINJA_FOR_RE.lastIndex = 0;
    let match;
    while ((match = JINJA_FOR_RE.exec(part)) !== null) {
      locals.add(match[1]);
      found.add(match[2]);
    }

    JINJA_IF_RE.lastIndex = 0;
    while ((match = JINJA_IF_RE.exec(part)) !== null) {
      found.add(match[1]);
    }

    JINJA_IF_IN_RE.lastIndex = 0;
    while ((match = JINJA_IF_IN_RE.exec(part)) !== null) {
      found.add(match[1]);
    }

    JINJA_VAR_RE.lastIndex = 0;
    while ((match = JINJA_VAR_RE.exec(part)) !== null) {
      found.add(match[1]);
    }
  }
  return [...found].filter((name) => !locals.has(name) && !JINJA_RESERVED.has(name));
}

/** Build `{ required: [...] }` from template text fields (stable sort). */
export function variablesSchemaFromTemplate(tmpl) {
  if (!tmpl) return { required: [] };
  const required = extractJinjaVars(
    tmpl.subject_template,
    tmpl.html_template,
    tmpl.text_template,
  ).sort();
  return { required };
}

/**
 * Sample payload for preview. Prefer canned samples for known codes; otherwise
 * bind schema + vars found in the template body to known/placeholder values.
 */
export function sampleFor(tmpl) {
  if (!tmpl) return {};
  if (DEFAULT_SAMPLE_BY_CODE[tmpl.code]) {
    return { ...DEFAULT_SAMPLE_BY_CODE[tmpl.code] };
  }
  const fromSchema = tmpl.variables_schema_json?.required || [];
  const fromContent = extractJinjaVars(
    tmpl.subject_template,
    tmpl.html_template,
    tmpl.text_template,
  );
  const keys = [...new Set([...fromSchema, ...fromContent])];
  return Object.fromEntries(
    keys.map((k) => [k, SAMPLE_VALUE_BY_VAR[k] !== undefined ? SAMPLE_VALUE_BY_VAR[k] : `<${k}>`]),
  );
}

/** Picker list: common catalog ∪ schema ∪ vars already used in the draft. */
export function insertVariablesFor(tmpl) {
  const fromSchema = tmpl?.variables_schema_json?.required || [];
  const fromContent = extractJinjaVars(
    tmpl?.subject_template,
    tmpl?.html_template,
    tmpl?.text_template,
  );
  return [...new Set([...COMMON_INSERT_VARIABLES, ...fromSchema, ...fromContent])].sort();
}
